import React from 'react';
import { Link } from 'react-router-dom';

import info from '../info.json';
import EmailIcon from '../icons/Email';
import GithubIcon from '../icons/Github';
import LinkedinIcon from '../icons/Linkedin';
import LocationIcon from '../icons/Location';
import TwitterIcon from '../icons/Twitter';
import ProfilePicture from '../components/ProfilePicture';
import MarkdownLayout from '../components/MarkdownLayout';
import EducationMarkdown from '../markdown/education.md';
import ProjectsMarkdown from '../markdown/projects.md';

type SectionLabelProps = { children: React.ReactNode; index?: string };
const SectionLabel: React.FC<SectionLabelProps> = ({ children, index }) => (
    <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent dark:text-accent-soft mb-5 flex items-baseline gap-3 select-none">
        {index && <span className="text-ink-400 dark:text-ink-500">{index}</span>}
        <span>{children}</span>
        <span aria-hidden className="flex-1 h-px bg-ink-200 dark:bg-ink-700 self-center" />
    </h2>
);

type ContactItemProps = {
    icon: React.ReactNode;
    label?: string;
    value: string;
    href?: string;
};
const ContactItem: React.FC<ContactItemProps> = ({ icon, label, value, href }) => {
    const valueNode = (
        <span className="text-ink-800 dark:text-ink-100 group-hover:text-accent dark:group-hover:text-accent-soft transition-colors break-all">
            {value}
        </span>
    );
    const inner = (
        <>
            <span className="w-4 flex items-center text-ink-400 dark:text-ink-500 shrink-0">{icon}</span>
            {label && (
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400 dark:text-ink-500 w-14 shrink-0">
                    {label}
                </span>
            )}
            {valueNode}
        </>
    );
    if (href) {
        return (
            <a
                href={href}
                target={href.startsWith('http') ? '_blank' : undefined}
                rel="noreferrer"
                className="group flex items-baseline gap-3 no-underline"
            >
                {inner}
            </a>
        );
    }
    return <div className="flex items-baseline gap-3">{inner}</div>;
};

const Landing: React.FC = () => (
    <main className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-12 py-16 sm:py-24">
        <header className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-5 gap-y-8 items-start sm:grid-cols-[13rem_minmax(0,1fr)] sm:grid-rows-[auto_auto_1fr] sm:gap-x-12 sm:gap-y-6">
            <ProfilePicture src="/img/profile.jpg" name={info.PROFILE_NAME} className="w-24 h-full sm:w-32 sm:h-32" />
            <div className="col-span-2 min-w-0 sm:col-span-1 sm:col-start-2 sm:row-span-3">
                <h1 className="font-display font-light text-[42px] sm:text-[56px] text-ink-900 dark:text-ink-50 tracking-tight leading-[1.05]">
                    {info.PROFILE_NAME}
                </h1>
                <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-ink-500 dark:text-ink-400 flex items-center gap-3">
                    <span>{info.PROFILE_DESCRIPTION}</span>
                    <span aria-hidden className="w-1 h-1 rounded-full bg-accent dark:bg-accent-soft" />
                    <a
                        href="/doc/CV.pdf"
                        target="_blank"
                        rel="noreferrer"
                        className="text-accent dark:text-accent-soft no-underline hover:underline decoration-accent/50 underline-offset-2"
                    >
                        CV
                    </a>
                    <span aria-hidden className="w-1 h-1 rounded-full bg-ink-300 dark:bg-ink-700" />
                    <Link
                        to="/journal"
                        className="text-accent dark:text-accent-soft no-underline hover:underline decoration-accent/50 underline-offset-2"
                    >
                        Journal
                    </Link>
                </p>
                <div className="mt-6 text-ink-800 dark:text-ink-100 leading-relaxed space-y-4 text-[15px] sm:text-justify sm:[hyphens:auto]">
                    <p>
                        I'm a protocol and full-stack engineer. Most of my recent work has been on Polkadot, Ethereum,
                        Tezos and Midnight, a privacy-preserving network.
                    </p>
                    <p>
                        As a core maintainer of Moonbeam and Frontier, I helped bring EVM compatibility to Polkadot. I
                        was also an early contributor to Acurast, a decentralized compute network that turns smartphones
                        into confidential compute nodes.
                    </p>
                    <p>
                        Earlier in my career I maintained SmartPy, one of the main smart contract languages on Tezos.
                        That work gave me a close look at how people actually write and use smart contracts.
                    </p>
                    <p>
                        Before Web3, I spent two years building a docking guidance system for pilots that's now in use
                        at airports.
                    </p>
                </div>
            </div>
            <div className="col-start-2 row-start-1 sm:col-start-1 sm:row-start-2">
                <h3 className="sr-only sm:not-sr-only sm:mb-3 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 dark:text-ink-500 select-none">
                    Contact
                </h3>
                <div className="space-y-2 text-[13px]">
                    <ContactItem
                        icon={<EmailIcon width={14} height={14} />}
                        value={info.E_MAIL}
                        href={`mailto:${info.E_MAIL}`}
                    />
                    <ContactItem
                        icon={<LinkedinIcon width={14} height={14} />}
                        value={info.LINKEDIN}
                        href={`https://www.linkedin.com/${info.LINKEDIN}`}
                    />
                    <ContactItem
                        icon={<TwitterIcon width={14} height={14} />}
                        value={`@${info.TWITTER}`}
                        href={`https://twitter.com/${info.TWITTER}`}
                    />
                    <ContactItem
                        icon={<GithubIcon width={14} height={14} />}
                        value={info.GIT}
                        href={`https://github.com/${info.GIT}`}
                    />
                    <ContactItem icon={<LocationIcon width={14} height={14} />} value={info.LOCATION} />
                </div>
            </div>
            <div className="col-span-2 sm:col-span-1">
                <h3 className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 dark:text-ink-500 mb-3 select-none">
                    Education
                </h3>
                <div className="text-[13px]">
                    <MarkdownLayout markdown={EducationMarkdown} />
                </div>
            </div>
        </header>

        <section className="mt-20">
            <SectionLabel>Previous Work</SectionLabel>
            <MarkdownLayout markdown={ProjectsMarkdown} />
        </section>
    </main>
);

export default Landing;
